import { captureException } from "@sentry/react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
  useLoaderData,
} from "react-router";
import DevBar from "./components/DevBar";

export async function loader() {
  const development = process.env.NODE_ENV === "development";
  return { development };
}

// Document shell. React Router wraps both the default export and the
// ErrorBoundary in Layout, so the error page also ships <Scripts /> (App Bridge
// loads, hydration matches). Do not call useLoaderData here — it renders even
// when a loader failed.
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  // Route responses (Shopify OAuth/token bounce, billing redirect, 4xx/5xx) are
  // expected control flow — don't report them to Sentry, only genuine errors.
  if (process.env.NODE_ENV === "production" && !isRouteErrorResponse(error)) {
    captureException(error);
  }
  return (
    <div>
      <p>⚠️ Something went wrong.</p>
      <p>
        If you keep seeing this message, please contact Exploding Discount
        support at{" "}
        <a
          href="mailto:help@salesboostersquad.com"
          target="_blank"
          rel="noreferrer"
        >
          help@salesboostersquad.com
        </a>
        .
      </p>
    </div>
  );
};

export default function App() {
  const { development } = useLoaderData<typeof loader>();
  return (
    <>
      {development ? <DevBar /> : null}
      <Outlet />
    </>
  );
}
